import { useState, useEffect, useRef } from 'react';

const DEFAULT_TYPING_SPEED_MS = 2; // milliseconds per character chunk
const DEFAULT_CHUNK_SIZE = 10; // characters per animation frame

/**
 * Custom hook for creating a typing animation effect
 *
 * @param {string} content - The full content to display
 * @param {boolean} isActive - Whether the typing animation should be active
 * @param {Object} options - Configuration options
 * @param {number} options.typingSpeed - Speed in milliseconds between chunks
 * @param {number} options.chunkSize - Number of characters to type per frame
 * @returns {string} The currently displayed content with typing animation
 */
const useTypingAnimation = (content, isActive, options = {}) => {
    const { typingSpeed = DEFAULT_TYPING_SPEED_MS, chunkSize = DEFAULT_CHUNK_SIZE } = options;

    const [displayedContent, setDisplayedContent] = useState('');
    const animationFrameRef = useRef(null);

    useEffect(() => {
        // If no content, reset displayed content
        if (!content) {
            setDisplayedContent('');
            return;
        }

        // If animation is not active, show full content immediately
        if (!isActive) {
            setDisplayedContent(content);
            return;
        }

        // If animation is active, animate new content
        const fullContent = content;
        const currentDisplayed = displayedContent;

        const hasContentChanged = !fullContent.startsWith(currentDisplayed);

        // If content has changed completely, reset and start from beginning
        if (hasContentChanged && currentDisplayed.length > 0) {
            setDisplayedContent('');
            return;
        }

        // If content has changed and there's new content to display
        if (fullContent.length > currentDisplayed.length) {
            let currentIndex = currentDisplayed.length;

            const typeNextChunk = () => {
                if (currentIndex < fullContent.length) {
                    // Type multiple characters at once for smoother animation
                    const actualChunkSize = Math.min(chunkSize, fullContent.length - currentIndex);
                    currentIndex += actualChunkSize;
                    setDisplayedContent(fullContent.substring(0, currentIndex));

                    animationFrameRef.current = setTimeout(typeNextChunk, typingSpeed);
                }
            };

            typeNextChunk();
        }

        // Cleanup function to clear timeout on unmount or when dependencies change
        return () => {
            if (animationFrameRef.current) {
                clearTimeout(animationFrameRef.current);
            }
        };
    }, [content, isActive, displayedContent, typingSpeed, chunkSize]);

    return displayedContent;
};

export default useTypingAnimation;
